import type React from "react";
import type { Excursion, ExcursionStatus } from "api/excursions/Excursion.typed";

export function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function isSameDay(a: Date, excursionDate: string): boolean {
  return toDateKey(a) === excursionDate.slice(0, 10);
}

export function parseHour(timeStr: string | null | undefined): number {
  if (!timeStr || !/^\d{1,2}:/.test(timeStr)) return 0;
  const parts = timeStr.split(":");
  return parseInt(parts[0], 10) || 0;
}

export function parseTimeToMinutes(timeStr: string | null | undefined): number {
  if (!timeStr || !/^\d{1,2}/.test(timeStr)) return 0;
  const parts = timeStr.trim().split(":");
  const h = parseInt(parts[0], 10) || 0;
  const m = parts.length > 1 ? parseInt(parts[1], 10) || 0 : 0;
  return h * 60 + m;
}

export function excursionToMinutes(ex: Excursion): { startMinutes: number; endMinutes: number } {
  const start = parseTimeToMinutes(ex.start_time);
  let end = parseTimeToMinutes(ex.end_time);
  if (end <= start) end = start + 60;
  return { startMinutes: start, endMinutes: end };
}

export function assignLanes<T>(
  items: T[],
  getStart: (t: T) => number,
  getEnd: (t: T) => number,
): { lane: number; totalLanes: number }[] {
  const withIndex = items.map((e, i) => ({ i, start: getStart(e), end: getEnd(e) }));
  withIndex.sort((a, b) => a.start - b.start || a.end - b.end);
  const laneEnds: number[] = [];
  const laneByIndex: number[] = new Array(items.length);
  for (const { i, start, end } of withIndex) {
    let lane = 0;
    while (lane < laneEnds.length && laneEnds[lane] > start) lane++;
    if (lane === laneEnds.length) laneEnds.push(end);
    else laneEnds[lane] = end;
    laneByIndex[i] = lane;
  }
  const totalLanes = laneEnds.length;
  return laneByIndex.map((lane) => ({ lane, totalLanes }));
}

export function excursionOverlapsHour(ex: Excursion, day: Date, hour: number): boolean {
  if (!isSameDay(day, ex.date)) return false;
  const startH = parseHour(ex.start_time);
  const endH = ex.end_time != null && ex.end_time !== "" ? parseHour(ex.end_time) : startH + 1;
  return hour >= startH && hour < endH;
}

export function hourToTimeStr(h: number): string {
  return `${String(h).padStart(2, "0")}:00`;
}

export function minutesToTimeStr(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export const HALF_HOUR = 30;

export function roundToHalfHour(minutes: number): number {
  return Math.round(minutes / HALF_HOUR) * HALF_HOUR;
}

export function getExcursionStatusEmoji(status: ExcursionStatus | undefined): string {
  if (status === "validated") return "✅";
  if (status === "cancelled") return "❌";
  return "📅";
}

export function getClosestStartTimeForDay(day: Date): string {
  const now = new Date();
  const today = toDateKey(now);
  const dayStr = toDateKey(day);
  if (dayStr > today) return "08:00";
  if (dayStr < today) return "08:00";
  const totalMins = now.getHours() * 60 + now.getMinutes();
  const nextSlot = Math.ceil((totalMins + 1) / HALF_HOUR) * HALF_HOUR;
  const slotMins = Math.min(nextSlot, 24 * 60 - HALF_HOUR);
  return minutesToTimeStr(slotMins);
}

export function addOneHour(timeStr: string): string {
  const mins = parseTimeToMinutes(timeStr);
  const next = mins + 60;
  if (next >= 24 * 60) return "23:30";
  return minutesToTimeStr(next);
}

/**
 * Duplicate on drop when a copy modifier is held at drop time.
 * Uses getModifierState (reliable during DnD on macOS; metaKey/ctrlKey are often false on drop).
 * Alt/Option: matches the “+” copy cursor with copyMove sources.
 */
export function planningTooltipDragWantsCopy(e: React.DragEvent<HTMLElement>): boolean {
  if (typeof e.getModifierState === "function") {
    return e.getModifierState("Control") || e.getModifierState("Meta") || e.getModifierState("Alt");
  }

  return e.ctrlKey || e.metaKey || e.altKey;
}

/** Cursor during drag over group / card when source used effectAllowed copyMove. */
export function setPlanningTooltipPersonChipDropEffect(e: React.DragEvent<HTMLElement>): void {
  const wantsCopy = planningTooltipDragWantsCopy(e);
  const allowed = e.dataTransfer.effectAllowed as string;
  const canCopy =
    allowed === "copy" ||
    allowed === "copyMove" ||
    allowed === "all" ||
    allowed === "copyLink" ||
    allowed === "copyLinkMove";
  e.dataTransfer.dropEffect = wantsCopy && canCopy ? "copy" : allowed === "copy" ? "copy" : "move";
}

/** Replaces updated excursions in the planning list without refetching the whole period. */
export function mergeExcursionsAfterPut(
  prev: Excursion[],
  updated: Excursion[],
  bounds: { from: string; to: string },
): Excursion[] {
  if (updated.length === 0) return prev;

  const incomingIds = new Set(updated.map((e) => e.id));

  const filtered = prev.filter((e) => !incomingIds.has(e.id));

  const toAdd = updated.filter((e) => {
    const d = e.date.slice(0, 10);

    return d >= bounds.from && d <= bounds.to;
  });

  const combined = [...filtered, ...toAdd];

  return combined.sort((a, b) => {
    const da = a.date.slice(0, 10).localeCompare(b.date.slice(0, 10));
    if (da !== 0) return da;

    const sa = a.start_time ?? "";
    const sb = b.start_time ?? "";
    const ts = sa.localeCompare(sb);
    if (ts !== 0) return ts;

    return a.id - b.id;
  });
}
